import { Fragment, type ReactElement, type ReactNode } from 'react';
import { Inline } from '@/ui/layout/Inline';
import { PageHeader } from '@/ui/layout/PageHeader';
import { Section } from '@/ui/layout/Section';
import { Stack } from '@/ui/layout/Stack';
import { PageShell } from '@/ui/primitives';
import { detailPageSectionGapToken } from './detailPageSpacing';
import type { SpacingToken } from '@/theme/tokens';

type ListPageTemplateProps = {
  title: ReactNode;
  titleActions?: ReactNode;
  headerActions?: ReactNode;
  headerActionsMode?: 'inline' | 'separate';
  hideHeader?: boolean;
  showHeaderDivider?: boolean;
  summary?: ReactNode;
  charts?: ReactNode;
  content?: ReactNode;
  footer?: ReactNode;
  headerSectionGap?: SpacingToken;
  paddingTop?: SpacingToken;
  paddingBottom?: SpacingToken;
};

export const ListPageTemplate = ({
  title,
  titleActions,
  headerActions,
  headerActionsMode = 'inline',
  hideHeader = false,
  showHeaderDivider: _showHeaderDivider = true,
  summary,
  charts,
  content,
  footer,
  headerSectionGap = 'md',
  paddingTop = 'lg',
  paddingBottom = '3xl',
}: ListPageTemplateProps): ReactElement => {
  const resolvedTitle =
    headerActionsMode === 'separate' ? (
      title
    ) : (
      <Inline gap="lg" align="center" wrap="wrap">
        {title}
        {titleActions}
      </Inline>
    );
  const sections = [
    {
      key: 'header-actions',
      node: headerActionsMode === 'separate' ? headerActions : null,
    },
    { key: 'summary', node: summary },
    { key: 'charts', node: charts },
    { key: 'content', node: content },
  ].filter((section): section is { key: string; node: ReactNode } => section.node != null);

  return (
    <PageShell
      maxWidth="xl"
      paddingX="2xl"
      paddingY="xl"
      paddingTop={paddingTop}
      paddingBottom={paddingBottom}
    >
      <Stack gap={headerSectionGap}>
        {hideHeader ? null : (
          <Section paddingY="xs">
            <PageHeader
              title={resolvedTitle}
              actions={headerActionsMode === 'separate' ? titleActions : headerActions}
              align="start"
            />
          </Section>
        )}
        {sections.length > 0 ? (
          <Stack gap={detailPageSectionGapToken}>
            {sections.map((section) => (
              <Fragment key={section.key}>{section.node}</Fragment>
            ))}
          </Stack>
        ) : null}
      </Stack>
      {footer}
    </PageShell>
  );
};
